import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useIsFetching, useIsMutating } from 'react-query';
import { useLocation } from 'react-router-dom';

const LoadingContext = createContext();

export const useLoading = () => {
  const context = useContext(LoadingContext);
  if (!context) {
    throw new Error('useLoading must be used within a LoadingProvider');
  }
  return context;
};

export const LoadingProvider = ({ children }) => {
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [shouldShowLoading, setShouldShowLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState('Loading...');
  const location = useLocation();
  const progressIntervalRef = useRef(null);
  const prevLocationRef = useRef(location.pathname);
  const loadingStartTimeRef = useRef(null);
  const delayTimerRef = useRef(null);
  
  // Track React Query fetching states
  const isFetching = useIsFetching();
  const isMutating = useIsMutating();

  useEffect(() => {
    // Detect route change
    if (location.pathname !== prevLocationRef.current) {
      prevLocationRef.current = location.pathname;
      setIsPageLoading(true);
      setShouldShowLoading(false);
      setLoadingProgress(10);
      //setLoadingMessage('Loading page...');
      loadingStartTimeRef.current = Date.now();
      
      // Clear any existing timers/intervals
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
      if (delayTimerRef.current) {
        clearTimeout(delayTimerRef.current);
      }
      
      // Only show loading screen if loading takes more than 1 second
      delayTimerRef.current = setTimeout(() => {
        const elapsed = Date.now() - loadingStartTimeRef.current;
        if (elapsed >= 1000 && isPageLoading) {
          setShouldShowLoading(true);
          
          // Simulate progress for page navigation
          let progress = 10;
          progressIntervalRef.current = setInterval(() => {
            progress += 5;
            if (progress <= 70) {
              setLoadingProgress(progress);
            } else {
              clearInterval(progressIntervalRef.current);
            }
          }, 30);
        }
      }, 1000);
    }
  }, [location.pathname, isPageLoading]);

  useEffect(() => {
    // Track data fetching
    if (isFetching > 0 || isMutating > 0) {
      if (!isPageLoading) {
        setIsPageLoading(true);
        setLoadingProgress(20);
        loadingStartTimeRef.current = Date.now();
        setShouldShowLoading(false);
        
        // Only show loading screen if loading takes more than 1 second
        if (delayTimerRef.current) {
          clearTimeout(delayTimerRef.current);
        }
        delayTimerRef.current = setTimeout(() => {
          const elapsed = Date.now() - loadingStartTimeRef.current;
          if (elapsed >= 1000 && (isFetching > 0 || isMutating > 0)) {
            setShouldShowLoading(true);
          }
        }, 1000);
      }
      
      // Update progress and message based on fetching state
      if (isFetching > 0) {
        //setLoadingMessage('Loading data...');
        setLoadingProgress(prev => {
          if (prev < 80) {
            return Math.min(prev + 10, 80);
          }
          return prev;
        });
      }
      
      if (isMutating > 0) {
        setLoadingMessage('Processing...');
        setLoadingProgress(prev => {
          if (prev < 85) {
            return Math.min(prev + 5, 85);
          }
          return prev;
        });
      }
    } else {
      // No fetching, complete the loading
      if (isPageLoading) {
        // Clear timers
        if (progressIntervalRef.current) {
          clearInterval(progressIntervalRef.current);
          progressIntervalRef.current = null;
        }
        if (delayTimerRef.current) {
          clearTimeout(delayTimerRef.current);
          delayTimerRef.current = null;
        }
        
        // Check if loading was quick (less than 1 second)
        const elapsed = loadingStartTimeRef.current ? Date.now() - loadingStartTimeRef.current : 0;
        
        if (elapsed < 1000) {
          // Loading was quick, don't show loading screen
          setIsPageLoading(false);
          setShouldShowLoading(false);
          setLoadingProgress(0);
        } else {
          // Loading took longer, show completion
          setLoadingProgress(100);
          const timer = setTimeout(() => {
            setIsPageLoading(false);
            setShouldShowLoading(false);
            setLoadingProgress(0);
          }, 500);
          
          return () => clearTimeout(timer);
        }
      }
    }
  }, [isFetching, isMutating, isPageLoading]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
      if (delayTimerRef.current) {
        clearTimeout(delayTimerRef.current);
      }
    };
  }, []);

  const setLoading = (loading, message = 'Loading...', progress = null) => {
    setIsPageLoading(loading);
    if (message) setLoadingMessage(message);
    if (progress !== null) setLoadingProgress(progress);
  };

  const value = {
    isPageLoading: shouldShowLoading && isPageLoading, // Only show if shouldShowLoading is true
    loadingProgress,
    loadingMessage,
    setLoading,
  };

  return (
    <LoadingContext.Provider value={value}>
      {children}
    </LoadingContext.Provider>
  );
};

